import { Modal, ModalBody, ModalHeader } from '@vd/ui';
import styles from './ShortcutsCheatsheet.module.css';

export interface ShortcutsCheatsheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Comprehensive keyboard-shortcut reference modal.
 *
 * Discoverability fix: shortcuts are scattered across the app
 * (project board, single view, ticket detail, annotation canvas)
 * and there's no other way to learn about them. Press `?` anywhere
 * a shortcut handler is mounted, or click the keyboard icon in the
 * board toolbar.
 *
 * Add new shortcuts here when you wire them up so users can find them.
 */
export function ShortcutsCheatsheet({ open, onClose }: ShortcutsCheatsheetProps) {
  return (
    <Modal open={open} onClose={onClose} width={640}>
      <ModalHeader>Keyboard shortcuts</ModalHeader>
      <ModalBody>
        <div className={styles.grid}>
          <Section title="Project pages (Board, Cards, Table, Single)">
            <Row keys={['1']} action="Switch to Kanban view" />
            <Row keys={['2']} action="Switch to Cards view" />
            <Row keys={['3']} action="Switch to Table view" />
            <Row keys={['4']} action="Switch to Single view" />
            <Row keys={['V']} action="Cycle through views" />
            <Row keys={['T']} action="Toggle thumbnails" />
            <Row keys={['/']} action="Focus the search bar" />
            <Row keys={['Esc']} action="Clear search / dismiss filters" />
          </Section>

          <Section title="Single view (queue mode)">
            <Row keys={['J', '↓']} action="Next ticket" />
            <Row keys={['K', '↑']} action="Previous ticket" />
            <Row keys={['Enter']} action="Open the selected ticket in full view" />
            <Row keys={['Esc']} action="Close queue, return to board" />
          </Section>

          <Section title="Ticket detail">
            <Row keys={['[']} action="Previous ticket in this project" />
            <Row keys={[']']} action="Next ticket in this project" />
            <Row keys={['⌘', 'Enter']} action="Save and return to source tab (capture flow only)" />
          </Section>

          <Section title="Annotation canvas (when editing a screenshot)">
            <Row keys={['V']} action="Pan tool (drag image)" />
            <Row keys={['P']} action="Pen" />
            <Row keys={['H']} action="Highlighter" />
            <Row keys={['C']} action="Circle" />
            <Row keys={['A']} action="Arrow" />
            <Row keys={['T']} action="Text (double-T opens text defaults)" />
            <Row keys={['R']} action="Redact" />
            <Row keys={['B']} action="Blur" />
            <Row keys={['E']} action="Eraser" />
            <Row keys={['Hold Space']} action="Temporary pan while held" />
            <Row keys={['⌘', 'Z']} action="Undo" />
            <Row keys={['⌘', '⇧', 'Z']} action="Redo" />
          </Section>

          <Section title="Anywhere">
            <Row keys={['?']} action="Open this cheatsheet" />
          </Section>
        </div>

        <div className={styles.foot}>
          Shortcuts ignore key presses while you're typing in an input or editing
          a comment, so your text isn't going to fight the bindings.
        </div>
      </ModalBody>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.rows}>{children}</div>
    </section>
  );
}

function Row({ keys, action }: { keys: string[]; action: string }) {
  return (
    <div className={styles.row}>
      <div className={styles.action}>{action}</div>
      <div className={styles.keys}>
        {keys.map((k, i) => (
          <kbd key={i} className={styles.key}>{k}</kbd>
        ))}
      </div>
    </div>
  );
}
